import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApolloQueryResult } from 'apollo-client';
import { defaultTo } from 'lodash-es';

import { Nullable } from '@rbi-ctg/frontend';
import { IPluDataQuery, PluDataDocument } from 'generated/rbi-graphql';
import { IPosDataQuery, PosDataDocument } from 'generated/sanity-graphql';
import { useApolloClient } from 'state/graphql/hooks/use-sanity-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { transformPlus } from 'utils/menu';
import { useSanityGqlEndpoint } from 'utils/network';
import { parseStringifiedJSON } from 'utils/parse-string';

import getRestaurantPlusData from './get-restaurant-plus-data';
import getRestaurantPosDataPlus from './get-restaurant-pos-data-plus';

type PosDataResults = {
  posData: Record<string, number> | null;
  disableChickenSandwich: boolean;
};

type PosDataIds = (ids?: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
}) => Promise<PosDataResults | null>;

interface IUsePosData {
  data: PosDataResults;
  refetch: PosDataIds;
}

const transformResponse = ({
  queryResponse,
  enableMenuServicePlus,
  serviceMode,
}: {
  queryResponse: ApolloQueryResult<IPluDataQuery & IPosDataQuery>;
  enableMenuServicePlus: boolean;
  serviceMode: Nullable<ServiceMode>;
}): PosDataResults => {
  let rawPlus = null;
  let disableChickenSandwich = false;

  if (enableMenuServicePlus) {
    rawPlus = getRestaurantPlusData(queryResponse?.data, serviceMode);
  } else {
    disableChickenSandwich =
      queryResponse?.data?.RestaurantPosData?.disableChickenSandwich ?? false;
    rawPlus = getRestaurantPosDataPlus(queryResponse?.data, serviceMode);
  }

  if (!rawPlus) {
    return { disableChickenSandwich, posData: null };
  }

  return {
    disableChickenSandwich,
    posData: transformPlus(parseStringifiedJSON({ defaultValue: {}, value: rawPlus })),
  };
};

export const usePosDataQuery = ({
  restaurantPosDataId,
  storeNumber,
  lazy = false,
}: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
  lazy?: boolean;
}): IUsePosData => {
  const enableMenuServicePlus = defaultTo(
    useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_PLUS),
    false
  );
  const { serviceMode } = useServiceModeContext();
  const sanityUri = useSanityGqlEndpoint();
  const client = useApolloClient();

  const [data, setData] = useState<PosDataResults>({
    disableChickenSandwich: false,
    posData: null,
  });

  const fetchPosData: PosDataIds = useCallback(
    async ids => {
      let queryResponse: ApolloQueryResult<any>;
      const sn = ids?.storeNumber ?? storeNumber;
      const id = ids?.restaurantPosDataId ?? restaurantPosDataId;

      if (enableMenuServicePlus) {
        if (!sn) {
          return Promise.resolve({ disableChickenSandwich: false, posData: null });
        }

        queryResponse = await client.query({
          query: PluDataDocument,
          variables: {
            storeId: sn,
          },
        });
      } else {
        if (!id) {
          return Promise.resolve({ disableChickenSandwich: false, posData: null });
        }

        queryResponse = await client.query({
          context: { uri: sanityUri },
          query: PosDataDocument,
          variables: {
            id,
          },
        });
      }

      const transformedResponse = transformResponse({
        queryResponse,
        enableMenuServicePlus,
        serviceMode,
      });
      setData(transformedResponse);
      return transformedResponse;
    },
    [client, enableMenuServicePlus, restaurantPosDataId, sanityUri, serviceMode, storeNumber]
  );

  useEffect(() => {
    if (!lazy) {
      fetchPosData({
        restaurantPosDataId,
        storeNumber,
      });
    }
  }, [fetchPosData, lazy, restaurantPosDataId, storeNumber]);

  return useMemo(
    () => ({
      data,
      refetch: fetchPosData,
    }),
    [data, fetchPosData]
  );
};
